import { ArrowPathIcon, PaperClipIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { type Dispatch, type FC, type SetStateAction, useEffect, useRef, useState } from "react";

import { presignTestFile } from "@api/rest/prompt-sandbox";

export interface RioAttachment {
  file_uuid: string;
  filename: string;
  mime_type: string;
}

/** Template uploads scope to `templateId`; prompt uploads use
 *  `shared: true` and land in the global-shared pool. */
export type RioAttachmentScope = { kind: "template"; templateId: number } | { kind: "prompt"; orgId?: string | null };

// Include both MIME types and file extensions — Chrome on Windows can
// hide DOCX from the picker when only the MIME is listed.
const ACCEPT = [
  "application/pdf",
  ".pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".docx",
  "image/png",
  ".png",
  "image/jpeg",
  ".jpg",
  ".jpeg",
  "image/webp",
  ".webp",
  "image/gif",
  ".gif",
].join(",");

/** Chip row above the composer; renders nothing when there are no
 *  attachments so it doesn't take vertical space during typing. */
export const RioAttachmentChips: FC<{
  attachments: RioAttachment[];
  onRemove: (file_uuid: string) => void;
  disabled?: boolean;
}> = ({ attachments, onRemove, disabled }) => {
  if (attachments.length === 0) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1">
      {attachments.map((a) => (
        <span
          key={a.file_uuid}
          title={a.filename}
          className="inline-flex max-w-[16rem] items-center gap-1 rounded border border-border-subtle bg-limestone-50 px-2 py-0.5 text-xs text-text-primary"
        >
          <PaperClipIcon className="size-3 shrink-0 text-text-tertiary" />
          <span className="truncate">{a.filename}</span>
          <button
            type="button"
            onClick={() => onRemove(a.file_uuid)}
            disabled={disabled}
            aria-label={`Remove ${a.filename}`}
            className="text-text-tertiary hover:text-text-primary disabled:opacity-40"
          >
            <XCircleIcon className="size-3.5" />
          </button>
        </span>
      ))}
    </div>
  );
};

/** Icon-only paperclip button + hidden file input. Owns the presign +
 *  S3 PUT so the parent only sees the resulting attachment metadata.
 *
 *  `onChange` accepts either the next array or a functional updater —
 *  the functional form is used during multi-file uploads so partial
 *  successes still land in the chip row and a mid-upload chip removal
 *  can't be undone by the closure of an earlier PUT. Callers who pass
 *  `useState`'s setter get this for free.
 *
 *  `onUploadingChange` lets the parent disable Send while an upload
 *  is in flight — without it, the turn would go out with the file
 *  missing and the chip would land on a later message. */
export const RioAttachButton: FC<{
  onChange: Dispatch<SetStateAction<RioAttachment[]>>;
  scope: RioAttachmentScope;
  disabled?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}> = ({ onChange, scope, disabled, onUploadingChange }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const mime = file.type || "application/octet-stream";
        const presign = await presignTestFile({
          ...(scope.kind === "template"
            ? { template_id: scope.templateId }
            : { shared: true, org_id: scope.orgId ?? null }),
          file_kind: "RIO_ATTACHMENT",
          mime_type: mime,
          original_filename: file.name,
        });
        const putResp = await fetch(presign.upload_url, {
          method: "PUT",
          headers: { "Content-Type": mime },
          body: file,
        });
        if (!putResp.ok) throw new Error(`S3 PUT failed (${putResp.status})`);
        // Append per-file so a later failure doesn't discard earlier
        // successes, and a mid-upload chip removal wins over this closure.
        const uploaded: RioAttachment = {
          file_uuid: presign.file_uuid,
          filename: file.name,
          mime_type: mime,
        };
        onChange((prev) => [...prev, uploaded]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        title={uploading ? "Uploading…" : "Attach a PDF, DOCX, or image"}
        aria-label="Attach a file"
        className="inline-flex items-center justify-center rounded-md border border-border-subtle bg-bg-default p-2 text-text-secondary transition-colors hover:cursor-pointer hover:bg-limestone-100 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-bg-default disabled:hover:text-text-secondary"
      >
        {uploading ? <ArrowPathIcon className="size-4 animate-spin" /> : <PaperClipIcon className="size-4" />}
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        onChange={(e) => void handleFiles(e.target.files)}
        className="hidden"
      />
    </>
  );
};
